// src/pages/CampaignDashboard/CampaignDashboardPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

// Import local components and utilities
import '../../App.css';
import HeaderMainPage from '../../header/HeaderMainPage';
import { fetchCampaignCompanies } from '../managingPage/CompanyDataUtils';
import ExplainDISC from '../utils/ExplainDISC';
import ExplainOCEAN from '../utils/ExplainOCEAN';
import CampaignSummary from "../managingPage/campaignSummary";
import CampaignHeader from './CampaignHeader';
import ProspectsTable from './ProspectsTable';
import PaginationControls from './PaginationControls';
import ReviewEmailModal from './modals/ReviewEmailModal';
import AddProspectModal from './modals/AddProspectModal';
import PromptSelectionModal from './modals/PromptSelectionModal';
import AutopilotModal from './modals/AutopilotModal';
import SetupMeetingModal from './modals/SetupMeetingModal';

const CampaignDashboardPage = () => {
    // ===== CORE HOOKS & SETUP =====
    const navigate = useNavigate();
    const { campaignId } = useParams();
    const roboman_api = process.env.REACT_APP_ROBOMAN_API;
    const authToken = localStorage.getItem("access_token");

    // ===== STATE MANAGEMENT =====
    const [localCompaniesData, setLocalCompaniesData] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});
    const [autopilotStatus, setAutoPilotStatus] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [numProspecttoView, setNumProspecttoView] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [numOfProspect, setNumOfProspect] = useState(0);

    // Modal Visibility State
    const [showReviewPopup, setShowReviewPopup] = useState(false);
    const [showAddContactPopup, setShowAddContactPopup] = useState(false);
    const [showPromptBox, setShowPromptBox] = useState(false);
    const [showAutoPilotPanel, setShowAutoPilotPanel] = useState(false);
    const [showSetupMeetingPopup, setShowSetupMeetingPopup] = useState(false);
    const [showCampaignSummary, setShowCampaignSummary] = useState(false);
    const [isDISCModalOpen, setIsDISCModalOpen] = useState(false);
    const [isOCEANModalOpen, setIsOCEANModalOpen] = useState(false);


    // Modal & Action-specific Data State
    const [reviewEmailData, setReviewEmailData] = useState([]);
    const [promptData, setPromptData] = useState([]);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [generatePromptAction, setGeneratePromptAction] = useState(false);
    const [selectedPromptAction, setSelectedPromptAction] = useState(null);

    // const [emailData, setEmailData] = useState([]);
    const [showActionMenu, setShowActionMenu] = useState(false);
    const toggleActionMenu = () => {
    setShowActionMenu(!showActionMenu);

        };

    
    const [emailData, setEmailData] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    let currentEmail = emailData[currentIndex];
    const [newContact, setNewContact] = useState({
        company_name: '',
        industry: '',
        company_email: '',
        company_linkedin: '',
        rep_name: '',
        rep_email: '',
        rep_linked: '',
      });
    const [calendlyLink, setCalendlyLink] = useState("");
    const campaign_id = localStorage.getItem("campaign_id");

    // ===== HELPER FUNCTIONS =====
    const getStatusStyle = (status) => {
        switch (status) {
            case 1: return { style: 'bg-gray-100 text-gray-600', text: 'Not Started' };
            case 2: return { style: 'bg-blue-100 text-blue-600', text: 'Waiting to Review' };
            case 3: return { style: 'bg-yellow-100 text-yellow-600', text: 'Reviewed' };
            case 4: return { style: 'bg-green-100 text-green-600', text: 'Sent' };
            case 5: return { style: 'bg-purple-100 text-purple-600', text: 'Opened' };
            case 6: return { style: 'bg-pink-100 text-pink-600', text: 'Replied' };
            case 7: return { style: 'bg-gray-100 text-gray-600', text: 'Generating' };
            case 8: return { style: 'bg-red-100 text-red-600', text: 'Generate Failed' };
            case 9: return { style: 'bg-gray-100 text-gray-600', text: 'Sending' };
            case 10: return { style: 'bg-red-100 text-red-600', text: 'Send Failed' };
            default: return { style: 'bg-transparent text-black', text: 'Unknown' };
        }
    };


    // ===== DATA FETCHING & API CALLS =====
    const updateData = async (page, pageSize) => {
        const data = await fetchCampaignCompanies(campaignId, page, pageSize, authToken);
        setLocalCompaniesData(data || []);
    };

    const fetchCompanyCount = async () => {
        try {
            const response = await fetch(`${roboman_api}/campaign/${campaignId}/companies/count`, { headers: { 'Accept': 'application/json', 'Authorization': authToken } });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            return data.company_count;
        } catch (error) {
            console.error("Failed to fetch company count:", error);
            Swal.fire('Fetch Failed', 'Could not retrieve the total number of prospects.', 'error');
            return null;
        }
    };

    const handleGetAutoPilotStatus = async () => {
        try {
            const response = await fetch(`${roboman_api}/campaign/${campaignId}/outreach/get-auto-status`, { headers: { 'Accept': 'application/json', 'Authorization': authToken }});
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            return data.auto_outreach;
        } catch (error) {
            console.error('Error fetching auto outreach status:', error);
            return null;
        }
    };

    const generateEmail = async (company_id_list, prompt_name) => {
        try {
            const response = await fetch(`${roboman_api}/campaign/emails/generate`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Authorization': authToken, 'Content-Type': 'application/json' },
                body: JSON.stringify({ company_id_list, campaign_id: campaignId, prompt_name }),
            });
            return response.ok;
        } catch (error) {
            console.error('Error during email generation:', error);
            return false;
        }
    };

    const sendEmail = async (company_id_list) => {
        try {
            const response = await fetch(`${roboman_api}/campaign/emails/send`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Authorization': authToken, 'Content-Type': 'application/json' },
                body: JSON.stringify({ company_id_list }),
            });
            if (!response.ok) console.error('API Error on sendEmail:', await response.text());
        } catch (error) {
            console.error('Email sending error:', error);
        }
    };

    const updateEmailStatus = async (companyId, contentSubject, content, emailConfirmationStatusId) => {
        try {
        const response = await fetch(`${roboman_api}/campaign/companies/update-email-status`, {
            method: 'PUT',
            headers: {
            'accept': 'application/json',
            'Authorization': localStorage.getItem("access_token"),
            'Content-Type': 'application/json'
            },
            body: JSON.stringify({
            company_id: companyId,
            content_subject: contentSubject,
            content: content,
            email_confirmation_status_id: emailConfirmationStatusId
            })
        });

        if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return null;
        }

        const data = await response.json();
        console.log("Update Successfully")
        return data;

        } catch (error) {
        console.error('Error updating email status:', error);
        return null;
        }
    }


    // ===== SIDE EFFECTS (useEffect) =====
    useEffect(() => {
        const initializeDashboard = async () => {
            const status = await handleGetAutoPilotStatus();
            setAutoPilotStatus(status);
            const count = await fetchCompanyCount();
            if (count !== null) {
                setNumOfProspect(count);
                setTotalPages(Math.ceil(count / numProspecttoView));
            }
        };
        initializeDashboard();
    }, [campaignId]);

    useEffect(() => {
        updateData(currentPage, numProspecttoView);
    }, [currentPage, numProspecttoView, campaignId]);


    // ===== EVENT HANDLERS =====
    const handleBack = () => {
        localStorage.removeItem("campaign_id");
        localStorage.removeItem("campaign_name");
        navigate('/my-campaigns');
    };

    const handleRefreshPage = () => updateData(currentPage, numProspecttoView);
    const handleMailboxClick = () => navigate("/unibox");
    const handleUpdateCampaign = () => navigate("/update-campaign");
    const handleAddMatchingCompany = () => navigate("/addmatching");

    const handleSelectAll = (event) => {
        const isChecked = event.target.checked;
        setSelectAll(isChecked);
        const newSelected = {};
        localCompaniesData.forEach(row => { newSelected[row.company_id] = isChecked; });
        setSelectedRows(newSelected);
    };

    const handleRowSelect = (id) => {
        const newSelected = { ...selectedRows, [id]: !selectedRows[id] };
        setSelectedRows(newSelected);
        const allSelected = localCompaniesData.every(row => newSelected[row.company_id]);
        setSelectAll(allSelected);
    };

    const handleGenerate = async (company_id) => {
        Swal.fire({ title: 'Generating Email...', text: 'Please wait.', icon: 'info', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        const prompt_type = selectedPrompt ? selectedPrompt.prompt_name : "email_generation_v1";
        const success = await generateEmail([company_id], prompt_type);
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait for backend processing
        Swal.close();
        if (success) {
            Swal.fire('Success!', 'Email generated.', 'success').then(() => updateData(currentPage, numProspecttoView));
        } else {
            Swal.fire('Error!', 'Failed to generate email.', 'error');
        }
    };
    
    const handleReview = async (company_id) => {
        try {
            const response = await fetch(`${roboman_api}/campaign/emails/draft/${company_id}`, { headers: { "accept": "application/json", "Authorization": authToken }});
            if (!response.ok) throw new Error('Failed to fetch drafts');
            const data = await response.json();
            setReviewEmailData(data);
            setShowReviewPopup(true);
        } catch (error) {
            Swal.fire("Error", "Could not fetch email drafts.", "error");
        }
    };
    
    const handleSend = async (company_id) => {
        Swal.fire({ title: 'Sending...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        await sendEmail([company_id]);
        await new Promise(resolve => setTimeout(resolve, 3000));
        Swal.close();
        updateData(currentPage, numProspecttoView);
    };

    const handleGenerateSelected = async () => {
        const selectedIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
        if (selectedIds.length === 0) return Swal.fire('No Selection', 'Please select rows to generate.', 'warning');
        
        Swal.fire({ title: 'Generating Selected Emails...', icon: 'info', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        const prompt_type = selectedPromptAction ? selectedPromptAction.prompt_name : (selectedPrompt ? selectedPrompt.prompt_name : "email_generation_v1");
        const success = await generateEmail(selectedIds, prompt_type);
        await new Promise(resolve => setTimeout(resolve, 5000 * selectedIds.length));
        Swal.close();
        
        if (success) {
            Swal.fire('Success!', `${selectedIds.length} emails are being generated.`, 'success').then(() => updateData(currentPage, numProspecttoView));
        } else {
            Swal.fire('Error!', 'Failed to generate emails.', 'error');
        }
        setSelectedRows({});
        setSelectAll(false);
    };

    const handleSendSelected = async () => {
        const selectedIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
        if (selectedIds.length === 0) return Swal.fire('No Selection', 'Please select rows to send.', 'warning');

        Swal.fire({ title: `Sending ${selectedIds.length} Emails...`, icon: 'info', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        await sendEmail(selectedIds);
        await new Promise(resolve => setTimeout(resolve, 3000));
        Swal.close();
        updateData(currentPage, numProspecttoView);
        setSelectedRows({});
        setSelectAll(false);
    };

    const handleDeleteSelected = async () => {
        if (showActionMenu === true) {
        toggleActionMenu();
        }

        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        // Get selected row IDs from the selectedRows object
        const selectedIds = Object.keys(selectedRows).filter((company_id) => selectedRows[company_id]);

        if (selectedIds.length === 0) {
        console.log("No rows selected.");
        Swal.fire({
            icon: "error",
            title: "No Rows Selected for Deletion",
            text: "Please select rows to delete.",
            confirmButtonColor: "#3085d6",
        });
        return;
        }

        const rowsToDelete = selectedIds.filter((company_id) => {
        const row = localCompaniesData.find((row) => row.company_id === company_id);
        return row; // Only keep valid rows
        });

        console.log("Rows to delete:", rowsToDelete);

        if (rowsToDelete.length === 0) {
        Swal.fire({
            icon: "error",
            title: "No Rows Found for Deletion",
            text: "There were no rows found to delete. Please select valid rows.",
            confirmButtonColor: "#3085d6",
        });
        return;
        }

        const batchSize = 3;
        console.log(`Processing ${rowsToDelete.length} rows in batches of ${batchSize}...`);

        let successCount = 0;
        let failureCount = 0;

        Swal.fire({
        title: "Deleting...",
        text: "Please wait while deleting records.",
        allowOutsideClick: false,
        didOpen: () => {
            Swal.showLoading();
        },
        });

        // Deleting in batches
        for (let i = 0; i < rowsToDelete.length; i += batchSize) {
        const batch = rowsToDelete.slice(i, i + batchSize);

        await Promise.all(
            batch.map(async (company_id) => {
            try {
                const response = await fetch(`${roboman_api}/campaign/companies/${company_id}/delete`, {
                method: 'DELETE',
                headers: {
                    'accept': 'application/json',
                    'Authorization': localStorage.getItem('access_token'),
                },
                });

                if (!response.ok) {
                throw new Error(`Failed to delete company_id: ${company_id}`);
                }

                console.log(`Successfully deleted company_id: ${company_id}`);
                successCount++;
            } catch (error) {
                console.error(`Error deleting company_id ${company_id}:`, error);
                failureCount++;
            }
            })
        );

        await delay(2000); // Delay between batch requests
        }

        Swal.close();
        // await updateData(currentPage, numProspecttoView); // Refresh the data after deletion
        setSelectedRows({}); // Reset selected rows
        setSelectAll(false); // Reset select all

        // Final summary message
        if (failureCount === 0) {
        Swal.fire({
            icon: "success",
            title: "All Records Deleted Successfully",
            text: `Successfully deleted ${successCount} records.`,
            confirmButtonColor: "#3085d6",
        }).then(() => {
            window.location.reload(); // Reload the page after confirmation
        });
        } else {
        Swal.fire({
            icon: "warning",
            title: "Some Records Failed to Delete",
            text: `Successfully deleted ${successCount} records. ${failureCount} deletions failed. Please review and try again.`,
            confirmButtonColor: "#3085d6",
        }).then(() => {
            window.location.reload(); // Reload the page after confirmation
        });
        }
    };
    
  const handleStopAutopilot = async () => {
  const campaignId = localStorage.getItem("campaign_id");

  try {
    const res = await fetch(`${roboman_api}/campaign/${campaignId}/outreach/stop-auto`, {
      method: 'PUT',
      headers: {
        accept: 'application/json',
        Authorization: localStorage.getItem('access_token'),
      },
    });

    if (res.ok) {
      setAutoPilotStatus(false);
      Swal.fire({ icon: 'success', title: 'Autopilot Disabled' });
    } else {
      Swal.fire({ icon: 'error', title: 'Failed to stop Autopilot' });
    }
    } catch (err) {
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Network Error' });
    }
    };


    const handleCancelReview = () => setShowReviewPopup(false);
    const handleUpdateMail = async () => {
        try {
        const response = await fetch(`${roboman_api}/campaign/emails/draft/update`, {
            method: 'PUT',
            headers: {
            'accept': 'application/json',
            'Authorization': localStorage.getItem('access_token'), // Replace with actual token
            'Content-Type': 'application/json',
            },
            body: JSON.stringify({
            company_id: currentEmail.company_id,
            draft_id: currentEmail.draft_id,
            subject: currentEmail.subject,
            body: currentEmail.body,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('Error updating email draft:', data);
            Swal.fire({
            title: 'Error!',
            text: data.message || 'Failed to update email draft.',
            icon: 'error',
            });
            return;
        }

        Swal.fire({
            title: 'Success!',
            text: 'Email draft updated successfully.',
            icon: 'success',
        });
        } catch (error) {
        console.error('Network or server error:', error);
        Swal.fire({
            title: 'Error!',
            text: 'An unexpected error occurred. Please try again.',
            icon: 'error',
        });
        }
    };
    
    const handleAddContact = async () => {
        // Check if required fields are filled
        const { company_name, industry, company_email, rep_email } = newContact;
        if (!company_name || !industry || !company_email || !rep_email) {
        Swal.fire({
            icon: 'warning',
            title: 'Missing Information',
            text: 'Please fill in all the required fields: Company Name, Industry, Company Email, and Reply Email.',
            confirmButtonText: 'OK',
        });
        return; // Stop the function execution if validation fails
        }

        try {
        // Prepare the contact data
        const contactData = {
            company_name: newContact.company_name,
            industry: newContact.industry,
            company_email: newContact.company_email,
            company_linkedin: decodeURIComponent(newContact.company_linkedin),
            rep_name: newContact.rep_name,
            rep_email: newContact.rep_email,
            rep_linkedin_address: decodeURIComponent(newContact.rep_linked),
        };

        // console.log("new contact data", contactData)

        // Send the data to your API (replace with actual API endpoint)
        const response = await fetch(`${roboman_api}/campaign/${localStorage.getItem("campaign_id")}/companies/add`, {
            method: 'POST',
            headers: {
            'accept': 'application/json',
            'Authorization': localStorage.getItem('access_token'), // Use your actual token here
            'Content-Type': 'application/json',
            },
            body: JSON.stringify(contactData),
        });

        if (!response.ok) {
            console.error('Failed to add contact:', response);
            Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: 'There was a problem adding the contact. Please try again later.',
            confirmButtonText: 'OK',
            });
            return;
        }

        // Optionally, get the response data if needed
        const data = await response.json();

        // Reset the form state
        setNewContact({
            company_name: '',
            industry: '',
            company_email: '',
            company_linkedin: '',
            rep_name: '',
            rep_email: '',
            rep_linked: '',
        });



        // Show success inform
        Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'The contact has been successfully added.',
            confirmButtonText: 'OK',
        }).then(() => {
            window.location.reload(); // Reload the page after confirmation
        });


        } catch (error) {
        console.error('Error adding contact:', error);
        Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: 'There was a problem adding the contact. Please try again later.',
            confirmButtonText: 'OK',
        });
        }
        // await updateData(currentPage, numProspecttoView);
    };

    const handlePromptCancel = () => {
        setShowPromptBox(false);
        setGeneratePromptAction(false);
    };

    const handlePromptConfirm = () => setShowPromptBox(false);
    const handleGenerateWithPrompt = () => {
        setShowPromptBox(false);
        handleGenerateSelected();
    };

    const handlePromptSelect = (prompt) => {
        if (generatePromptAction) {
            setSelectedPromptAction(prompt);
        } else {
            setSelectedPrompt(prompt);
        }
    };


    const handleAutoPilot = async () => {

        if (showAutoPilotPanel === true) { setShowAutoPilotPanel(false)}

        console.log("Start AutoPilot:");

        let prompt_type = "email_generation_v1"
        if (selectedPrompt != null) {
        prompt_type = selectedPrompt.prompt_name
        }

        try {
        const response = await fetch(`${roboman_api}/campaign/${campaign_id}/outreach/auto`, {
            method: "POST",
            headers: {
            'Accept': 'application/json',
            'Authorization': localStorage.getItem('access_token'),
            'Content-Type': 'application/json',
            },
            body: JSON.stringify({
            prompt_name: prompt_type
            }),
        }
        );

        if (!response.ok) {
            console.error("Failed to process AutoPilot.");
            await Swal.fire({
            title: "AutoPilot Failed",
            text: "We encountered an issue while processing AutoPilot. Please try again later.",
            icon: "error",
            confirmButtonText: "OK",
            });
            return;
        }

        await Swal.fire({
            title: "AutoPilot Started",
            text: "AutoPilot is now processing your request. Please refresh the page in a few minutes to check the status.",
            icon: "success",
            confirmButtonText: "OK",
        });
        } catch (error) {
        console.error("AutoPilot Error:", error);
        await Swal.fire({
            title: "AutoPilot Failed",
            text: "We encountered an issue while processing AutoPilot. Please try again later.",
            icon: "error",
            confirmButtonText: "OK",
        });
        }


        updateData(currentPage, numProspecttoView);
        setSelectedRows({});
        setSelectAll(false);

        // Reload the page
        window.location.reload();
        return;
    }

    const handleSetupMeeting = async () => {
        // Validate calendlyLink before making the API call
        if (!calendlyLink || !/^https:\/\/.+/.test(calendlyLink)) {
        Swal.fire({
            icon: 'warning',
            title: 'Invalid Link',
            text: 'The Calendly link is not in the correct format. Please enter a valid https link.',
        });
        return;
        }

        // call the API to setup meeting
        try {
        const response = await fetch(`${roboman_api}/campaign/${campaign_id}/update-calendly`,
            {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Authorization': localStorage.getItem('campaign_id'),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                calendly_link: calendlyLink,
            }),
            }
        );

        const data = await response.json();

        if (response.ok) {
            Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Calendly link updated successfully!',
            });
        } else {
            Swal.fire({
            icon: 'error',
            title: 'Update Failed',
            text: data?.message || 'Something went wrong while updating the link.',
            });
        }
        } catch (error) {
        Swal.fire({
            icon: 'error',
            title: 'Network Error',
            text: error.message || 'An unexpected error occurred.',
        });
        }
    };
    
    const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(p => p - 1); };
    const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(p => p + 1); };
    

    // ===== JSX RENDER =====
    return (
        <div className="bg-white min-h-screen flex flex-col items-center">
            <HeaderMainPage />
            <div className="w-full max-w-[1900px] h-full mx-auto mt-10 bg-white rounded-lg shadow-lg">
                <CampaignHeader
                    campaignName={localStorage.getItem("campaign_name")}
                    autopilotStatus={autopilotStatus}
                    onBack={handleBack}
                    onRefreshPage={handleRefreshPage}
                    onViewSummary={() => setShowCampaignSummary(true)}
                    onMailboxClick={handleMailboxClick}
                    onUpdateCampaign={handleUpdateCampaign}
                    onAddMatchingCompany={handleAddMatchingCompany}
                    onAddContact={() => setShowAddContactPopup(true)}
                    onPromptConfig={() => setShowPromptBox(true)}
                    onSetupMeeting={() => setShowSetupMeetingPopup(true)}
                    onGenerateSelected={handleGenerateSelected}
                    onGenerateWithPrompt={() => { setGeneratePromptAction(true); setShowPromptBox(true); }}
                    onSendEmailSelected={handleSendSelected}
                    onDeleteSelected={handleDeleteSelected}
                    onStartAutopilot={() => setShowAutoPilotPanel(true)}
                    onStopAutopilot={handleStopAutopilot}
                    onSetNumProspectToView={setNumProspecttoView}
                />

                <div className="overflow-x-auto">
                    <div className="h-[calc(100vh-350px)] overflow-y-auto">
                        <ProspectsTable
                            prospects={localCompaniesData}
                            selectAll={selectAll}
                            selectedRows={selectedRows}
                            onSelectAll={handleSelectAll}
                            onRowSelect={handleRowSelect}
                            onGenerate={handleGenerate}
                            onReview={handleReview}
                            onSend={handleSend}
                            getStatusStyle={getStatusStyle}
                        />
                    </div>
                    <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPrevPage={handlePrevPage}
                        onNextPage={handleNextPage}
                    />
                </div>

                {/* --- Modals --- */}
                <ExplainDISC isOpen={isDISCModalOpen} closeModal={() => setIsDISCModalOpen(false)} />
                <ExplainOCEAN isOpen={isOCEANModalOpen} closeModal={() => setIsOCEANModalOpen(false)} />
                
                {showCampaignSummary && <CampaignSummary campaignId={campaignId} onClose={() => setShowCampaignSummary(false)} />}
                {showReviewPopup && <ReviewEmailModal isOpen={showReviewPopup} initialEmailData={reviewEmailData} onClose={handleCancelReview} onUpdate={handleUpdateMail} />}
                {showAddContactPopup && <AddProspectModal isOpen={showAddContactPopup} onClose={() => setShowAddContactPopup(false)} onAddContact={handleAddContact} />}
                {showPromptBox && <PromptSelectionModal isOpen={showPromptBox} onClose={handlePromptCancel} onConfirm={generatePromptAction ? handleGenerateWithPrompt : handlePromptConfirm} promptData={promptData} selectedPrompt={generatePromptAction ? selectedPromptAction : selectedPrompt} onPromptSelect={handlePromptSelect} isGenerateAction={generatePromptAction} />}
                {showAutoPilotPanel && <AutopilotModal isOpen={showAutoPilotPanel} onClose={() => setShowAutoPilotPanel(false)} onStart={handleAutoPilot} promptData={promptData} onPromptSelect={handlePromptSelect} selectedPrompt={selectedPrompt} />}
                {showSetupMeetingPopup && <SetupMeetingModal isOpen={showSetupMeetingPopup} onClose={() => setShowSetupMeetingPopup(false)} onConfirm={handleSetupMeeting} />}
            </div>
        </div>
    );
};

export default CampaignDashboardPage;